// api.ts — REST wrappers for the Phase 2 backend manager (backend/manager).
// The manager exposes /sessions[/...] and forwards to the per-user agent
// container; identity comes from the Auth0 bearer (auth.md), not the URL.

import type {
	CreateSessionResponse,
	ListSessionsResponse,
	MessagesResponse,
	HistoryEntry,
} from './agent-protocol';
import { authHeaders } from './auth.svelte';

// Re-export so existing importers (marketplace-api.ts) keep getting it from './api'.
export { authHeaders };

function backendUrl(): string {
	const raw = (import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:8787') as string;
	return raw.replace(/\/$/, '');
}

// Exposed so ws-client + marketplace-api can derive their origins from the
// same base instead of each re-reading VITE_BACKEND_URL.
export const API_ORIGIN: string = backendUrl();


// Shape consumed by sessions-store + sidebar + chat route. Phase 2's session
// model is thinner than Phase 1's; we synthesise the fields the UI reads.
export interface SessionInfo {
	sessionId: string;
	state: 'starting' | 'connected' | 'running' | 'exited';
	cwd: string;
	createdAt: number;
	lastActivityAt?: number;
	name?: string;
	// Soft-delete marker (Trash, SAY-1463): set ⇒ trashed (hidden from the sidebar,
	// shown in the Trash list, restorable until permanently deleted).
	deletedAt?: number;
}

function sessionsBase(): string {
	// Identity is the bearer, not the URL — no `/users/:user` segment anymore.
	return `${backendUrl()}/sessions`;
}

export async function jsonOrThrow<T>(res: Response, label: string): Promise<T> {
	if (!res.ok) {
		let detail = res.statusText;
		try {
			const body = (await res.json()) as { error?: string };
			if (body.error) detail = body.error;
		} catch {
			/* noop */
		}
		throw new Error(`${label}: ${res.status} ${detail}`);
	}
	return res.json() as Promise<T>;
}

export async function listSessions(): Promise<SessionInfo[]> {
	const res = await fetch(sessionsBase(), { headers: await authHeaders() });
	const body = await jsonOrThrow<ListSessionsResponse>(res, 'listSessions');
	return body.sessions.map((s) => ({
		sessionId: s.id,
		state: 'connected',
		cwd: '',
		createdAt: s.createdAt,
		lastActivityAt: s.lastActivityAt,
		name: s.name,
		deletedAt: s.deletedAt,
	}));
}

export async function createSession(opts?: {
	provider?: 'claude' | 'codex';
	model?: string;
	effort?: string;
	/** Chat folder to start the chat in (SAY-1820): the manager records the
	 *  membership and hands the folder's instructions to the agent. */
	folderId?: string;
}): Promise<SessionInfo> {
	// The manager mints the session id + worktree; permission mode is hardcoded
	// (bypassPermissions) in the agent container. The POST normally carries no
	// body; an admin can override the provider/model/effort for a single new chat
	// (staging test), or the chat starts inside a folder, in which case we send a
	// JSON `{ provider?, model?, effort?, folderId? }` body — all fields are
	// optional server-side, so the bodyless default is unchanged.
	const hasOverride = Boolean(opts && (opts.provider || opts.model || opts.effort || opts.folderId));
	const headers = await authHeaders();
	const init: RequestInit = { method: 'POST', headers };
	if (hasOverride) {
		init.headers = { ...headers, 'content-type': 'application/json' };
		init.body = JSON.stringify({
			provider: opts?.provider,
			model: opts?.model,
			effort: opts?.effort,
			folderId: opts?.folderId,
		});
	}
	const res = await fetch(sessionsBase(), init);
	const body = await jsonOrThrow<CreateSessionResponse>(res, 'createSession');
	return {
		sessionId: body.id,
		state: 'connected',
		cwd: '',
		createdAt: Date.now(),
	};
}

// The model + reasoning settings pinned to an existing chat. GET is generic so
// the UI can honestly hide its GPT-only control for Claude sessions; PATCH is
// accepted by the backend only for an idle Codex session and takes effect on the
// next message without replacing the native Codex thread handle.
export interface SessionEngine {
	provider: 'claude' | 'codex';
	model: string;
	effort?: string;
}

export interface SessionEngineUpdate {
	model: string;
	effort: string;
}

export async function getSessionEngine(sessionId: string): Promise<SessionEngine> {
	const res = await fetch(`${sessionsBase()}/${encodeURIComponent(sessionId)}/engine`, {
		headers: await authHeaders(),
	});
	return jsonOrThrow<SessionEngine>(res, 'getSessionEngine');
}

export async function updateSessionEngine(
	sessionId: string,
	update: SessionEngineUpdate,
): Promise<SessionEngine> {
	const res = await fetch(`${sessionsBase()}/${encodeURIComponent(sessionId)}/engine`, {
		method: 'PATCH',
		headers: { ...(await authHeaders()), 'content-type': 'application/json' },
		body: JSON.stringify(update),
	});
	return jsonOrThrow<SessionEngine>(res, 'updateSessionEngine');
}

// PERMANENT delete (Trash → "Delete forever", SAY-1463). Drops the session, its
// history, and its worktree in the agent container — irreversible. The two-stage
// trash flow below is what the sidebar uses for the everyday case.
export async function deleteSession(sessionId: string): Promise<void> {
	const res = await fetch(`${sessionsBase()}/${sessionId}`, {
		method: 'DELETE',
		headers: await authHeaders(),
	});
	if (!res.ok && res.status !== 404) throw new Error(`deleteSession: ${res.status}`);
}

// Move a chat to the Trash (soft delete). Reversible via restoreSession; the chat
// stays intact (history + worktree) until deleteSession purges it.
// NOTE: unlike deleteSession, a 404 is NOT swallowed — for trash/restore a 404
// means the action did NOT take (e.g. the session is gone, or the agent image is
// stale and lacks the endpoint), so it must surface as an error and roll back the
// optimistic UI rather than showing a false "moved to Trash" success.
export async function trashSession(sessionId: string): Promise<void> {
	const res = await fetch(`${sessionsBase()}/${encodeURIComponent(sessionId)}/trash`, {
		method: 'POST',
		headers: await authHeaders(),
	});
	if (!res.ok) throw new Error(`trashSession: ${res.status}`);
}

// Restore a trashed chat back into the sidebar.
export async function restoreSession(sessionId: string): Promise<void> {
	const res = await fetch(`${sessionsBase()}/${encodeURIComponent(sessionId)}/restore`, {
		method: 'POST',
		headers: await authHeaders(),
	});
	if (!res.ok) throw new Error(`restoreSession: ${res.status}`);
}

// ── /sessions/search (⌘K search dialog) ──────────────────────────────────
// Mirror of backend/manager/src/session-search.ts SessionSearchResult. The
// manager searches titles + user messages + assistant responses of the
// caller's own chats and returns recency-ordered hits with short excerpts.
export interface SessionSearchSnippet {
	role: 'user' | 'assistant';
	at: number;
	text: string;
}

export interface SessionSearchResult {
	id: string;
	name: string | null;
	createdAt: number;
	lastActivityAt: number;
	titleMatch: boolean;
	matchCount: number;
	snippets: SessionSearchSnippet[];
}

export interface SessionSearchResponse {
	query: string;
	results: SessionSearchResult[];
}

// Thrown when the manager answers 404 for /sessions/search — i.e. it predates
// the route (the frontend and the manager deploy independently, and a local
// stack may run an older manager). The dialog then searches in the browser
// through the per-chat /messages endpoint instead of showing a dead search box.
export class SearchUnavailableError extends Error {
	constructor() {
		super('searchSessions: 404 (manager has no /sessions/search yet)');
		this.name = 'SearchUnavailableError';
	}
}

export async function searchSessions(query: string, signal?: AbortSignal): Promise<SessionSearchResponse> {
	const res = await fetch(`${sessionsBase()}/search?q=${encodeURIComponent(query)}`, {
		headers: await authHeaders(),
		signal,
	});
	if (res.status === 404) throw new SearchUnavailableError();
	return jsonOrThrow<SessionSearchResponse>(res, 'searchSessions');
}

export async function loadHistory(sessionId: string): Promise<HistoryEntry[]> {
	const res = await fetch(`${sessionsBase()}/${sessionId}/messages`, {
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<MessagesResponse>(res, 'loadHistory');
	return body.entries;
}

// ── /uploads (roadmap #1) ────────────────────────────────────────────────
// POSTs the file as a RAW binary stream to the manager. The filename rides on
// the `x-filename` header (URL-encoded — HTTP headers are ASCII, and the user
// may attach unicode/spaces/parens). We use XHR (not fetch) so the chip can
// show a real upload-progress bar — browser fetch has no upload-progress
// event.
//
// RELIABILITY: the upload completes BEFORE the user can send the message; the
// resulting `/data/uploads/<name>` path is what rides on the wire. The frame
// stored in ws-client.outstanding therefore contains only the final path, so
// a reconnect replays a byte-identical frame and the upload never re-runs.
export interface UploadResponse {
	path: string; // in-container path, e.g. "/data/uploads/report.pdf"
}

export async function uploadFile(
	file: File,
	onProgress?: (frac: number) => void,
): Promise<UploadResponse> {
	const headers = await authHeaders();
	return new Promise<UploadResponse>((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open('POST', `${API_ORIGIN}/uploads`);
		for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
		xhr.setRequestHeader('Content-Type', 'application/octet-stream');
		// URL-encode so spaces / unicode / parens survive the header.
		xhr.setRequestHeader('x-filename', encodeURIComponent(file.name));
		xhr.upload.onprogress = (e: ProgressEvent) => {
			if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
		};
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				try {
					const parsed = JSON.parse(xhr.responseText) as { path?: string; error?: string };
					if (parsed.path) resolve({ path: parsed.path });
					else reject(new Error('bad upload response'));
				} catch {
					reject(new Error('bad upload response'));
				}
			} else {
				// Surface the manager's error message if present (e.g. "invalid name").
				let detail = `${xhr.status}`;
				try {
					const body = JSON.parse(xhr.responseText) as { error?: string };
					if (body.error) detail = body.error;
				} catch {
					/* keep status as the detail */
				}
				reject(new Error(`upload failed: ${detail}`));
			}
		};
		xhr.onerror = () => reject(new Error('upload network error'));
		xhr.send(file);
	});
}

// Persist a user rename (SAY-1464). PATCHes the manager, which proxies to the
// owner's agent container; the container stores `name` (so a reload's listSessions
// keeps the chosen title) and broadcasts a session_name to any open tab. Returns
// the server-accepted (trimmed) name so the caller renders exactly what was stored.
export async function renameSession(
	sessionId: string,
	name: string,
): Promise<{ ok: boolean; name: string }> {
	const res = await fetch(`${sessionsBase()}/${encodeURIComponent(sessionId)}/name`, {
		method: 'PATCH',
		headers: { ...(await authHeaders()), 'content-type': 'application/json' },
		body: JSON.stringify({ name }),
	});
	const body = await jsonOrThrow<{ id: string; name: string }>(res, 'renameSession');
	return { ok: true, name: body.name };
}

// ── /folders (chat folders, SAY-1820) ────────────────────────────────────
// Mirror of backend/manager/src/chat-folders.ts. Folders + membership are
// per-user manager state; a chat started with `folderId` gets the folder's
// instructions appended to its operating prompt by the agent.
export interface ChatFolder {
	id: string;
	name: string;
	instructions: string;
	createdAt: number;
	updatedAt: number;
}

export interface ChatFoldersView {
	folders: ChatFolder[];
	/** sessionId → folderId */
	membership: Record<string, string>;
	/** Chats pinned to the top of their list. */
	pinned: string[];
}

function foldersBase(): string {
	return `${backendUrl()}/folders`;
}

export async function listFolders(): Promise<ChatFoldersView> {
	const res = await fetch(foldersBase(), { headers: await authHeaders() });
	return jsonOrThrow<ChatFoldersView>(res, 'listFolders');
}

export async function createFolder(input: { name: string; instructions?: string }): Promise<ChatFolder> {
	const res = await fetch(foldersBase(), {
		method: 'POST',
		headers: { ...(await authHeaders()), 'content-type': 'application/json' },
		body: JSON.stringify(input),
	});
	return jsonOrThrow<ChatFolder>(res, 'createFolder');
}

export async function updateFolder(
	id: string,
	patch: { name?: string; instructions?: string },
): Promise<ChatFolder> {
	const res = await fetch(`${foldersBase()}/${encodeURIComponent(id)}`, {
		method: 'PATCH',
		headers: { ...(await authHeaders()), 'content-type': 'application/json' },
		body: JSON.stringify(patch),
	});
	return jsonOrThrow<ChatFolder>(res, 'updateFolder');
}

export async function deleteFolder(id: string): Promise<{ released: string[] }> {
	const res = await fetch(`${foldersBase()}/${encodeURIComponent(id)}`, {
		method: 'DELETE',
		headers: await authHeaders(),
	});
	return jsonOrThrow<{ released: string[] }>(res, 'deleteFolder');
}

/** Move chats into a folder, or out of any folder with `folderId: null`. */
export async function moveSessionsToFolder(sessionIds: string[], folderId: string | null): Promise<ChatFoldersView> {
	const res = await fetch(`${foldersBase()}/move`, {
		method: 'POST',
		headers: { ...(await authHeaders()), 'content-type': 'application/json' },
		body: JSON.stringify({ sessionIds, folderId }),
	});
	return jsonOrThrow<ChatFoldersView>(res, 'moveSessionsToFolder');
}

/** Pin a chat to the top of its list (or unpin it). */
export async function setSessionPinned(sessionId: string, pinned: boolean): Promise<ChatFoldersView> {
	const res = await fetch(`${sessionsBase()}/${encodeURIComponent(sessionId)}/pin`, {
		method: 'PUT',
		headers: { ...(await authHeaders()), 'content-type': 'application/json' },
		body: JSON.stringify({ pinned }),
	});
	return jsonOrThrow<ChatFoldersView>(res, 'setSessionPinned');
}
